import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import type { Organization } from "@/contracts";
import { z } from "zod";

import { suspendOrganizationMutation } from "@/api/queries";
import { Alert } from "@/components/ui/alert";
import { TextAreaField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";
import { useApiClient } from "@/lib/api-context";

const suspendFormSchema = z.object({
  reason: z.string().trim().max(500),
});

type SuspendFormValues = z.infer<typeof suspendFormSchema>;

/**
 * Suspension is an operational decision we take, not something a customer
 * triggers, so the reason is for our own audit trail rather than for display.
 */
export function OrganizationSuspendDialog({
  organization,
  onClose,
}: {
  organization: Organization;
  onClose: () => void;
}) {
  const client = useApiClient();
  const { t } = useTranslation("pages");
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SuspendFormValues>({
    resolver: zodResolver(suspendFormSchema),
    defaultValues: { reason: "" },
  });

  const mutation = useMutation({
    ...suspendOrganizationMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    await mutation
      .mutateAsync({
        id: organization.id,
        body: { ...(values.reason ? { reason: values.reason } : {}) },
      })
      .then(onClose)
      // The alert below carries the failure; rethrowing would surface it as
      // an unhandled rejection as well.
      .catch(() => undefined);
  });

  return (
    <FormDialog
      open
      title={t("suspendNamedTitle", { name: organization.name })}
      submitLabel={t("suspendService")}
      destructive
      pending={isSubmitting || mutation.isPending}
      onSubmit={(event) => void onSubmit(event)}
      onCancel={onClose}
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}

        <Alert tone="warn" title={t("dialogSuspendWhatTitle")} announce={false}>
          {t("dialogSuspendWhatBody")}
        </Alert>

        <TextAreaField
          label={t("fieldReason")}
          hint={t("suspendReasonHint")}
          {...(errors.reason?.message === undefined ? {} : { error: errors.reason.message })}
          {...register("reason")}
        />
      </div>
    </FormDialog>
  );
}
